import moment from 'jalali-moment';
import * as Yup from 'yup';

import { buildOptionalNewPasswordFieldSchema } from '../../Shared/utils/passwordPolicy';

const toEnglishDigits = (value) =>
  String(value ?? '')
    .replace(/[۰-۹]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d))
    .replace(/[٠-٩]/g, (d) => '٠١٢٣٤٥٦٧٨٩'.indexOf(d));

const isJalaliYear = (year) => year >= 1200 && year < 1500;

const isValidMoment = (m) => m && typeof m.isValid === 'function' && m.isValid();

const gregorianToJalaliString = (raw) => {
  const formats = [
    'YYYY-MM-DD HH:mm:ss',
    'YYYY-MM-DD HH:mm',
    'YYYY-MM-DD',
    'YYYY/MM/DD',
  ];

  for (const fmt of formats) {
    const parsed = moment(raw, fmt);
    if (isValidMoment(parsed)) {
      return parsed.locale('fa').format('jYYYY/jM/jD');
    }
  }

  const loose = moment(raw);
  return isValidMoment(loose) ? loose.locale('fa').format('jYYYY/jM/jD') : '';
};

export const formatBirthDateForPicker = (value) => {
  const raw = toEnglishDigits(value).trim();
  if (!raw) {
    return '';
  }

  const slashMatch = raw.match(/^(\d{4})\/(\d{1,2})\/(\d{1,2})/);
  if (slashMatch) {
    const year = Number(slashMatch[1]);
    if (isJalaliYear(year)) {
      return `${slashMatch[1]}/${Number(slashMatch[2])}/${Number(slashMatch[3])}`;
    }
    if (year >= 1900) {
      return gregorianToJalaliString(
        `${slashMatch[1]}-${slashMatch[2]}-${slashMatch[3]}`,
      );
    }
  }

  const dashMatch = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (dashMatch) {
    const year = Number(dashMatch[1]);
    if (isJalaliYear(year)) {
      return `${dashMatch[1]}/${Number(dashMatch[2])}/${Number(dashMatch[3])}`;
    }
    if (year >= 1900) {
      return gregorianToJalaliString(
        `${dashMatch[1]}-${dashMatch[2]}-${dashMatch[3]}`,
      );
    }
  }

  return gregorianToJalaliString(raw);
};

export const buildProfileValidationSchema = (passwordPolicy) =>
  Yup.object().shape({
    first_name: Yup.string()
      .required('نام الزامی است')
      .max(100, 'نام نمی‌تواند بیشتر از 100 کاراکتر باشد'),
    last_name: Yup.string()
      .required('نام خانوادگی الزامی است')
      .max(100, 'نام خانوادگی نمی‌تواند بیشتر از 100 کاراکتر باشد'),
    father_name: Yup.string().nullable().max(150),
    mobile: Yup.string()
      .required('موبایل الزامی است')
      .matches(/^09\d{9}$/, 'شماره موبایل باید با 09 شروع شود و 11 رقم باشد'),
    phone: Yup.string().nullable().max(22),
    province_id: Yup.number()
      .typeError('انتخاب استان الزامی است')
      .required('انتخاب استان الزامی است'),
    city: Yup.string().nullable().max(190),
    gender: Yup.number()
      .typeError('انتخاب جنسیت الزامی است')
      .required('انتخاب جنسیت الزامی است'),
    birth_date: Yup.string()
      .nullable()
      .test(
        'birth-date-format',
        'تاریخ تولد را به درستی وارد نمایید',
        (val) => !val || /^\d{4}\/\d{1,2}\/\d{1,2}$/.test(val),
      ),
    password: buildOptionalNewPasswordFieldSchema(passwordPolicy),
    password_confirmation: Yup.string().when('password', {
      is: (val) => val && val.length > 0,
      then: (schema) =>
        schema
          .required('تکرار کلمه عبور الزامی است')
          .oneOf([Yup.ref('password')], 'کلمه عبور و تکرار آن یکسان نیست'),
      otherwise: (schema) => schema.nullable(),
    }),
  });

export const buildProfileInitialValues = (user) => ({
  first_name: user?.first_name ?? '',
  last_name: user?.last_name ?? '',
  father_name: user?.father_name ?? '',
  nat_code: user?.nat_code ?? '',
  mobile: user?.mobile ?? '',
  phone: user?.phone ?? '',
  province_id: user?.province_id ?? '',
  city: user?.city ?? '',
  gender:
    user?.gender === 0 || user?.gender === 1 || user?.gender === '0' || user?.gender === '1'
      ? Number(user.gender)
      : '',
  birth_date:
    formatBirthDateForPicker(user?.birth_date) || user?.birth_date || '',
  avatar: user?.avatar ?? '',
  password: '',
  password_confirmation: '',
});

export const prepareProfilePayload = (values, photoIds) => {
  const payload = { ...values };

  if (photoIds.length > 0) {
    const photoId = photoIds[0].serverId;
    const avatar = JSON.parse(photoId);
    payload.avatar = avatar.data.path;
  }

  if (!payload.password?.trim()) {
    delete payload.password;
    delete payload.password_confirmation;
  }

  if (!payload.birth_date?.trim()) {
    payload.birth_date = null;
  }

  delete payload.nat_code;

  return payload;
};
